import api from '../_api'
import { service as bilingPrimary } from '@/api/billing-primary'
import {
    FETCH_INOVICE_MONTHS_REQUEST,
    FETCH_INOVICE_MONTHS_SUCCESS,
    FETCH_INOVICE_MONTHS_FAILED,
    FETCH_INVOICE_ANNUAL_SUMMARY_REQUEST,
    FETCH_INVOICE_ANNUAL_SUMMARY_SUCCESS,
    FETCH_INVOICE_ANNUAL_SUMMARY_FAILED,
    DOWNLOAD_INVOICE_PDF_REQUEST,
    DOWNLOAD_INVOICE_PDF_SUCCESS,
    DOWNLOAD_INVOICE_PDF_FAILED,
    FETCH_BULK_LATEST_MONTH_REQUEST,
    FETCH_BULK_LATEST_MONTH_SUCCESS,
    FETCH_BULK_LATEST_MONTH_FAILED,
    REQUEST_BULK_DOWNLOAD_REQUEST,
    REQUEST_BULK_DOWNLOAD_SUCCESS,
    REQUEST_BULK_DOWNLOAD_FAILED,
    FETCH_BULK_JOBS_REQUEST,
    FETCH_BULK_JOBS_SUCCESS,
    FETCH_BULK_JOBS_FAILED,
    UPDATE_BULK_JOB
} from './mutation-type'

const getInvoiceMonths = async ({ commit, state, getters, rootGetters }, { usageAccountId }) => {
    commit(FETCH_INOVICE_MONTHS_REQUEST)
    try {
        const { status, data } = await api.fetchInvoiceMonths({ usageAccountId }, rootGetters.requiredService)
        commit(FETCH_INOVICE_MONTHS_SUCCESS, { status, data: data.data })
        return data
    } catch (error) {
        commit(FETCH_INOVICE_MONTHS_FAILED, error)
        throw error
    }
}

const getInvoiceAnnualSummary = async ({ commit, state, getters, rootGetters }, { usageAccountId, year }) => {
    commit(FETCH_INVOICE_ANNUAL_SUMMARY_REQUEST)
    try {
        const { status, data } = await api.fetchInvoiceAnnualSummary({ usageAccountId, year }, rootGetters.requiredService)
        commit(FETCH_INVOICE_ANNUAL_SUMMARY_SUCCESS, { status, data: data.data })
        return data
    } catch (error) {
        commit(FETCH_INVOICE_ANNUAL_SUMMARY_FAILED, error)
        throw error
    }
}

const downloadInvoicePDF = async ({ commit, state, getters, rootGetters }, { usageAccountId, month, locale }) => {
    commit(DOWNLOAD_INVOICE_PDF_REQUEST)
    try {
        const { data } = await bilingPrimary.invoicePDF({ usageAccountId, month, locale }, rootGetters.requiredService)
        commit(DOWNLOAD_INVOICE_PDF_SUCCESS, data)
        return data
    } catch (error) {
        commit(DOWNLOAD_INVOICE_PDF_FAILED)
        throw error
    }
}

/**
 * 요청 화면 진입 정보. 기간 상한 + 안내에 필요한 제약값을 함께 받는다.
 *
 * 보관 기간·메일 첨부 한계를 여기서 상수로 두지 않는 이유 — 서버 설정이 바뀌면
 * 화면 안내만 옛 값으로 남는다. 사용자는 안내를 믿고 요청하는데 실제 동작이 다르면
 * 그게 곧 문의가 된다. 제약을 정하는 쪽(서버)이 값도 내려준다(BO-964).
 *
 * 이번 요청이 첨부 한도를 넘을지는 받지 않는다 — 만들어봐야 알 수 있다.
 * 판단은 워커가 완료 시점에 실측으로 하고, 화면은 "한도가 얼마인지"만 미리 알린다.
 */
const getBulkLatestMonth = async ({ commit, rootGetters }, { groupIdx }) => {
    commit(FETCH_BULK_LATEST_MONTH_REQUEST)
    try {
        const { data } = await api.fetchBulkLatestMonth({ groupIdx }, rootGetters.requiredService)
        const info = data?.data ?? {}
        commit(FETCH_BULK_LATEST_MONTH_SUCCESS, {
            latestMonth: info.latestMonth ?? null,
            mailAttachLimitBytes: info.mailAttachLimitBytes ?? null,
            expireDays: info.expireDays ?? null
        })
        return info.latestMonth ?? null
    } catch (error) {
        commit(FETCH_BULK_LATEST_MONTH_FAILED, error)
        throw error
    }
}

const requestBulkDownload = async ({ commit, rootGetters }, { groupIdx, payload }) => {
    commit(REQUEST_BULK_DOWNLOAD_REQUEST)
    try {
        const { data } = await api.requestBulkDownload({ groupIdx, payload }, rootGetters.requiredService)
        commit(REQUEST_BULK_DOWNLOAD_SUCCESS)
        // jobId 만 꺼내지 않는다 — 응답에 계정이 없는 수신자 목록이 함께 온다.
        // 그 사람들은 용량 초과로 첨부가 빠지면 사이트에 들어올 수 없어 아예 못 받는다.
        // 요청 직후에 알려줘야 수신자를 바꿀 수 있다(BO-964).
        return {
            jobId: data?.data?.jobId ?? null,
            emailsWithoutAccount: data?.data?.emailsWithoutAccount ?? []
        }
    } catch (error) {
        commit(REQUEST_BULK_DOWNLOAD_FAILED, error)
        throw error
    }
}

const getBulkJobs = async ({ commit, rootGetters }) => {
    commit(FETCH_BULK_JOBS_REQUEST)
    try {
        const { data } = await api.fetchBulkJobs(rootGetters.requiredService)
        const jobs = data?.data ?? []
        commit(FETCH_BULK_JOBS_SUCCESS, jobs)
        return jobs
    } catch (error) {
        commit(FETCH_BULK_JOBS_FAILED, error)
        throw error
    }
}

/**
 * 폴링 1회. 실패해도 목록 전체를 무너뜨리지 않는다 —
 * 일시적인 오류로 진행 중이던 요청이 화면에서 사라지면 사용자가 더 혼란스럽다.
 */
const refreshBulkJob = async ({ commit, rootGetters }, { jobId }) => {
    try {
        const { data } = await api.fetchBulkJob({ jobId }, rootGetters.requiredService)
        const job = data?.data ?? null
        if (job) commit(UPDATE_BULK_JOB, job)
        return job
    } catch (error) {
        return null
    }
}

/**
 * 다운로드 URL 을 지금 발급받는다.
 *
 * 목록에 URL 을 미리 담아두지 않는 이유 — presigned URL 은 보유 자체가 권한이라,
 * 미리 받으면 사용자가 다운로드를 누르지 않아도 전 건의 URL 이 브라우저에 남는다.
 * 버튼을 누른 시점에만 짧은 URL 을 받아 곧바로 쓰고 버린다(BO-964).
 *
 * 스토어에 저장하지 않는다. 저장하면 미리 받아두는 것과 같아진다.
 */
const getBulkDownloadUrl = async ({ rootGetters }, { jobId, month }) => {
    const { data } = await api.fetchBulkDownloadUrl({ jobId, month }, rootGetters.requiredService)
    return data?.data ?? null
}

/**
 * 완료된 ZIP 을 메일로 보낸다(BO-983).
 *
 * 스토어에 상태를 두지 않는다 — 발송 결과는 목록을 다시 받으면 mailResult 로 반영된다.
 */
const sendBulkMail = async ({ rootGetters }, { jobId, month, emails }) => {
    const { data } = await api.sendBulkMail({ jobId, month, emails }, rootGetters.requiredService)
    return data?.data?.mailResult ?? null
}

export default {
    getInvoiceMonths,
    getInvoiceAnnualSummary,
    downloadInvoicePDF,
    getBulkLatestMonth,
    requestBulkDownload,
    getBulkJobs,
    refreshBulkJob,
    getBulkDownloadUrl,
    sendBulkMail
}
